// SQLite (sql.js) service for Ezbiz Invoice.
// All user data lives in the user-supplied .db file. This module owns the
// in-memory SQLite instance and provides CRUD that mirrors the React state.

import initSqlJs from "sql.js";

let SQL = null;
let dbInstance = null;

async function ensureSql() {
  if (SQL) return SQL;
  SQL = await initSqlJs({
    locateFile: () => `${process.env.PUBLIC_URL || ""}/sql-wasm.wasm`,
  });
  return SQL;
}

const SCHEMA = `
CREATE TABLE IF NOT EXISTS company (
  id INTEGER PRIMARY KEY CHECK (id = 1),
  data TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS parties (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  gstin TEXT,
  data TEXT NOT NULL,
  updated_at TEXT
);
CREATE INDEX IF NOT EXISTS idx_parties_name ON parties(name);

CREATE TABLE IF NOT EXISTS invoices (
  id TEXT PRIMARY KEY,
  number TEXT,
  date TEXT,
  customer_name TEXT,
  total REAL,
  data TEXT NOT NULL,
  created_at TEXT,
  updated_at TEXT
);
CREATE INDEX IF NOT EXISTS idx_invoices_updated_at ON invoices(updated_at);
CREATE INDEX IF NOT EXISTS idx_invoices_date ON invoices(date);
`;

export async function openFromBytes(uint8) {
  const sql = await ensureSql();
  dbInstance = uint8 ? new sql.Database(uint8) : new sql.Database();
  dbInstance.exec(SCHEMA);
  return dbInstance;
}

export async function newEmptyDb() {
  return openFromBytes(null);
}

export function isOpen() {
  return !!dbInstance;
}

export function exportBytes() {
  if (!dbInstance) return null;
  return dbInstance.export(); // Uint8Array
}

export function close() {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

function row(stmt) {
  const r = stmt.getAsObject();
  stmt.free();
  return r;
}

function all(stmt) {
  const out = [];
  while (stmt.step()) out.push(stmt.getAsObject());
  stmt.free();
  return out;
}

// ---------------- Company ----------------

export function getCompany() {
  if (!dbInstance) return null;
  const stmt = dbInstance.prepare("SELECT data FROM company WHERE id = 1");
  if (!stmt.step()) {
    stmt.free();
    return null;
  }
  const r = row(stmt);
  try {
    return JSON.parse(r.data);
  } catch {
    return null;
  }
}

export function saveCompany(company) {
  if (!dbInstance) return;
  const json = JSON.stringify(company || {});
  dbInstance.run(
    `INSERT INTO company (id, data) VALUES (1, ?)
     ON CONFLICT(id) DO UPDATE SET data = excluded.data`,
    [json],
  );
}

// ---------------- Parties ----------------

function partyRowToObject(r) {
  try {
    const data = JSON.parse(r.data);
    return { id: r.id, ...data };
  } catch {
    return { id: r.id, name: r.name };
  }
}

export function listParties() {
  if (!dbInstance) return [];
  const stmt = dbInstance.prepare(
    "SELECT id, name, gstin, data FROM parties ORDER BY name COLLATE NOCASE",
  );
  return all(stmt).map(partyRowToObject);
}

export function upsertParty(party) {
  if (!dbInstance) return null;
  const id = party.id || crypto.randomUUID();
  const name = party.name || "";
  const gstin = party.billTo?.gstin || party.gstin || "";
  const stored = { ...party, id };
  delete stored.id; // we already track id separately
  const json = JSON.stringify(stored);
  dbInstance.run(
    `INSERT INTO parties (id, name, gstin, data, updated_at)
     VALUES (?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET
       name = excluded.name,
       gstin = excluded.gstin,
       data = excluded.data,
       updated_at = excluded.updated_at`,
    [id, name, gstin, json, new Date().toISOString()],
  );
  return id;
}

export function deletePartyById(id) {
  if (!dbInstance || !id) return;
  dbInstance.run("DELETE FROM parties WHERE id = ?", [id]);
}

export function replaceAllParties(list) {
  if (!dbInstance) return;
  dbInstance.run("DELETE FROM parties");
  const now = new Date().toISOString();
  const stmt = dbInstance.prepare(
    `INSERT INTO parties (id, name, gstin, data, updated_at) VALUES (?, ?, ?, ?, ?)`,
  );
  for (const p of list || []) {
    const id = p.id || crypto.randomUUID();
    const name = p.name || "";
    const gstin = p.billTo?.gstin || p.gstin || "";
    const stored = { ...p, id };
    delete stored.id;
    stmt.run([id, name, gstin, JSON.stringify(stored), now]);
  }
  stmt.free();
}

// ---------------- Invoices ----------------

function invoiceRowToObject(r) {
  try {
    return JSON.parse(r.data);
  } catch {
    return null;
  }
}

export function listInvoices() {
  if (!dbInstance) return [];
  const stmt = dbInstance.prepare(
    "SELECT data FROM invoices ORDER BY updated_at DESC",
  );
  return all(stmt).map(invoiceRowToObject).filter(Boolean);
}

export function upsertInvoice(invoice) {
  if (!dbInstance) return null;
  const id = invoice.id || crypto.randomUUID();
  const now = new Date().toISOString();
  const stamped = {
    ...invoice,
    id,
    updatedAt: now,
    createdAt: invoice.createdAt || now,
  };
  const json = JSON.stringify(stamped);
  dbInstance.run(
    `INSERT INTO invoices (id, number, date, customer_name, total, data, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET
       number = excluded.number,
       date = excluded.date,
       customer_name = excluded.customer_name,
       total = excluded.total,
       data = excluded.data,
       updated_at = excluded.updated_at`,
    [
      id,
      stamped.meta?.number || "",
      stamped.meta?.date || "",
      stamped.customer?.name || "",
      Number(stamped.totals?.total || 0),
      json,
      stamped.createdAt,
      stamped.updatedAt,
    ],
  );
  return stamped;
}

export function deleteInvoiceById(id) {
  if (!dbInstance || !id) return;
  dbInstance.run("DELETE FROM invoices WHERE id = ?", [id]);
}

export function getLastInvoiceNumber() {
  if (!dbInstance) return null;
  const stmt = dbInstance.prepare(
    "SELECT number FROM invoices ORDER BY updated_at DESC LIMIT 1",
  );
  if (!stmt.step()) {
    stmt.free();
    return null;
  }
  const r = row(stmt);
  return r.number || null;
}
